import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from './AppKitAuthContext';
import { authService } from '../services/auth';
import { API_BASE_URL } from '../services/api';

// Re-export types from the original hook
export interface LeaderboardEntry {
  rank: number;
  walletAddress: string;
  gamesPlayed?: number;
  dxfMined?: string;
  streak: number;
  multiplier: number;
  finalScore: number | string;
}

export interface RewardConfig {
  minRank: number;
  maxRank: number;
  amount: string;
  type: 'BNB' | 'DXF';
}

export interface RewardsConfiguration {
  id?: string;
  name: string;
  bnbRewards: RewardConfig[];
  dxfRewards: RewardConfig[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WeeklyLeaderboardResponse {
  weekStartDate: string;
  weekEndDate: string;
  isActive: boolean;
  gameCountRanking: LeaderboardEntry[];
  dxfMiningRanking: LeaderboardEntry[];
  rewardsConfigId: string;
  rewardsConfig: RewardsConfiguration | null;
  userRank?: {
    gameCount: LeaderboardEntry;
    dxfMining: LeaderboardEntry;
  };
  createdAt: string;
  completedAt?: string;
}

export interface UserStreakResponse {
  currentStreak: number;
  lastStreak: number;
  bestStreak: number;
  multiplier: number;
  lastGameDate: string;
  eligibleToRecover: boolean;
  recoverStreakPayment?: string;
}

interface LeaderboardState {
  data: WeeklyLeaderboardResponse | null;
  userStreak: UserStreakResponse | null;
  loading: boolean;
  error: string | null;
}

interface LeaderboardContextValue extends LeaderboardState {
  refetch: () => Promise<void>;
  formatWalletAddress: (address: string) => string;
  formatDXFAmount: (weiAmount: string) => string;
  formatRewardAmount: (config: RewardConfig) => string;
  getWeekDisplayString: string;
}

const LeaderboardContext = createContext<LeaderboardContextValue | undefined>(undefined);

interface LeaderboardProviderProps {
  children: ReactNode;
  isActive?: boolean;
}

export const LeaderboardProvider: React.FC<LeaderboardProviderProps> = ({ 
  children, 
  isActive = true 
}) => {
  const [state, setState] = useState<LeaderboardState>({
    data: null,
    userStreak: null,
    loading: true,
    error: null
  });

  const { isAuthenticated } = useAuth();

  const fetchLeaderboardData = useCallback(async () => {
    if (!isActive) {
      setState({
        data: null,
        userStreak: null,
        loading: false,
        error: null
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Create headers with auth token if available
      const getHeaders = () => {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        
        const token = authService.getToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        return headers;
      };

      // Fetch leaderboard data (includes user ranking if authenticated)
      const leaderboardResponse = await fetch(`${API_BASE_URL}/leaderboard/current`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!leaderboardResponse.ok) {
        throw new Error(`Failed to fetch leaderboard: ${leaderboardResponse.status}`);
      }

      const leaderboardData: WeeklyLeaderboardResponse = await leaderboardResponse.json();

      // Fetch user streak if authenticated
      let userStreakData: UserStreakResponse | null = null;
      if (isAuthenticated) {
        try {
          const userStreakResponse = await fetch(`${API_BASE_URL}/leaderboard/user-streak`, {
            method: 'GET',
            headers: getHeaders(),
          });

          if (userStreakResponse.ok) {
            userStreakData = await userStreakResponse.json();
          }
        } catch (error) {
          console.warn('Failed to fetch user streak:', error);
          // Continue without user streak data - it's optional
        }
      }

      setState({
        data: leaderboardData,
        userStreak: userStreakData,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch leaderboard'
      }));
    }
  }, [isActive, isAuthenticated]);

  // Initial load
  useEffect(() => {
    fetchLeaderboardData();
  }, [fetchLeaderboardData]);

  // Helper function to format wallet address
  const formatWalletAddress = useCallback((address: string): string => {
    if (!address || address.length < 8) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, []);

  // Helper function to format DXF amount from wei
  const formatDXFAmount = useCallback((weiAmount: string): string => {
    try {
      const amount = parseFloat(weiAmount) / Math.pow(10, 18);
      if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(2)}M`;
      } else if (amount >= 1000) {
        return `${(amount / 1000).toFixed(2)}K`;
      } else if (amount >= 1) {
        return amount.toFixed(2);
      } else {
        return amount.toFixed(4);
      }
    } catch {
      return '0';
    }
  }, []);

  // Helper function to format reward amount
  const formatRewardAmount = useCallback((config: RewardConfig): string => {
    if (config.type === 'BNB') {
      // Convert from wei to BNB
      const bnbAmount = parseFloat(config.amount) / Math.pow(10, 18);
      return `${bnbAmount.toFixed(2)} BNB`;
    } else {
      // Convert from wei to DXF tokens 
      const dxfAmount = parseFloat(config.amount) / Math.pow(10, 18);
      if (dxfAmount >= 1000000) {
        return `${(dxfAmount / 1000000).toFixed(3)}M DXF`;
      } else if (dxfAmount >= 1000) {
        return `${(dxfAmount / 1000).toFixed(1)}K DXF`;
      } else {
        return `${dxfAmount.toFixed(0)} DXF`;
      }
    }
  }, []);

  const getWeekDisplayString = state.data 
    ? `${state.data.weekStartDate} - ${state.data.weekEndDate}`
    : '';

  const value: LeaderboardContextValue = {
    ...state,
    refetch: fetchLeaderboardData,
    formatWalletAddress,
    formatDXFAmount,
    formatRewardAmount,
    getWeekDisplayString,
  };

  return (
    <LeaderboardContext.Provider value={value}>
      {children}
    </LeaderboardContext.Provider>
  );
};

export const useLeaderboardContext = (): LeaderboardContextValue => {
  const context = useContext(LeaderboardContext);
  if (context === undefined) {
    throw new Error('useLeaderboardContext must be used within a LeaderboardProvider');
  }
  return context;
};